/**
 * BRAND CATEGORY MANAGER
 * 
 * A modal/panel to tag brands as indie/boutique/mainstream.
 * Shows all unique brands from the user's collection and lets them categorize.
 */

import React, { useState, useEffect } from 'react';
import { collection, doc, getDocs, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { X, Search, Loader2, Download } from 'lucide-react';
import { BRAND_CATEGORY_OPTIONS, findKnownCategory, type BrandCategory, type BrandCategoryInfo } from '../types/brandCategories';

interface BrandCategoryManagerProps {
  isOpen: boolean;
  onClose: () => void;
  brands: string[]; // All unique brands from collection
  onCategoriesUpdated?: () => void;
}

const BrandCategoryManager: React.FC<BrandCategoryManagerProps> = ({
  isOpen,
  onClose,
  brands,
  onCategoriesUpdated
}) => {
  const [brandCategories, setBrandCategories] = useState<Record<string, BrandCategory>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load existing brand categories
  useEffect(() => {
    if (!isOpen) return;

    const loadCategories = async () => {
      const user = auth.currentUser;
      if (!user) return;

      setLoading(true);
      try {
        const categoriesRef = collection(db, 'users', user.uid, 'brandCategories');
        const snapshot = await getDocs(categoriesRef);
        
        const categories: Record<string, BrandCategory> = {};
        snapshot.forEach((doc) => {
          const data = doc.data() as BrandCategoryInfo;
          categories[doc.id] = data.category;
        });
        
        setBrandCategories(categories);
      } catch (err) {
        console.error('Error loading brand categories:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, [isOpen]);

  if (!isOpen) return null;

  // Filter brands by search
  const filteredBrands = brands.filter(brand =>
    brand.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort: uncategorized first, then by name
  const sortedBrands = [...filteredBrands].sort((a, b) => {
    const aCat = brandCategories[a];
    const bCat = brandCategories[b];
    if (!aCat && bCat) return -1;
    if (aCat && !bCat) return 1;
    return a.localeCompare(b);
  });

  // Count stats
  const categorizedCount = brands.filter(b => brandCategories[b]).length;
  const indieCount = brands.filter(b => brandCategories[b] === 'indie').length;
  const boutiqueCount = brands.filter(b => brandCategories[b] === 'boutique').length;
  const mainstreamCount = brands.filter(b => brandCategories[b] === 'mainstream').length;

  // Save category for a brand
  const handleSetCategory = async (brand: string, category: BrandCategory | null) => {
    const user = auth.currentUser;
    if (!user) return;

    setSaving(brand);
    try {
      const brandDocId = brand.replace(/[\/\.]/g, '_'); // Sanitize for Firestore doc ID
      const brandRef = doc(db, 'users', user.uid, 'brandCategories', brandDocId);

      if (category) {
        await setDoc(brandRef, {
          brand,
          category,
          updatedAt: serverTimestamp()
        });
        setBrandCategories(prev => ({ ...prev, [brand]: category }));
      } else {
        // Remove category
        await deleteDoc(brandRef);
        setBrandCategories(prev => {
          const updated = { ...prev };
          delete updated[brand];
          return updated;
        });
      }

    } catch (err) {
      console.error('Error saving brand category:', err);
      alert('Error saving. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  // Auto-detect brands using known brand lists
  const handleAutoDetect = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setSaving('auto');
    let detectedCount = 0;
    
    try {
      for (const brand of brands) {
        // Skip if already categorized
        if (brandCategories[brand]) continue;
        
        // Check against known brand lists
        const detectedCategory = findKnownCategory(brand);
        
        if (detectedCategory) {
          const brandDocId = brand.replace(/[\/\.]/g, '_');
          const brandRef = doc(db, 'users', user.uid, 'brandCategories', brandDocId);
          await setDoc(brandRef, {
            brand,
            category: detectedCategory,
            updatedAt: serverTimestamp()
          });
          setBrandCategories(prev => ({ ...prev, [brand]: detectedCategory }));
          detectedCount++;
        }
      }
      
      if (detectedCount > 0) {
        alert(`Auto-detected ${detectedCount} brand${detectedCount === 1 ? '' : 's'}!`);
      } else {
        alert('No new brands detected. Your uncategorized brands may be unique to your collection!');
      }

    } catch (err) {
      console.error('Error auto-detecting:', err);
      alert('Error during auto-detection. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  // Export brand categories as JSON
  const handleExport = () => {
    const exportData = {
      exportDate: new Date().toISOString(),
      totalBrands: brands.length,
      categorized: Object.keys(brandCategories).length,
      categories: {
        indie: brands.filter(b => brandCategories[b] === 'indie'),
        boutique: brands.filter(b => brandCategories[b] === 'boutique'),
        mainstream: brands.filter(b => brandCategories[b] === 'mainstream'),
        uncategorized: brands.filter(b => !brandCategories[b])
      }
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `brand-categories-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="glass-modal max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-600">
          <div>
            <h2 className="text-xl font-bold text-glass font-heading">
              Categorize Your Brands
            </h2>
            <p className="text-sm text-glass-muted mt-1 font-body">
              Tag brands as indie, boutique, or mainstream for better stats
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800/40 rounded-full transition-default"
          >
            <X className="icon-md text-glass-muted" />
          </button>
        </div>

        {/* Stats bar */}
        <div className="px-4 py-3 bg-slate-800/60 border-b border-slate-600 flex flex-wrap gap-4 items-center">
          <div className="text-sm text-glass-secondary font-body">
            <span className="font-bold">{categorizedCount}</span> of <span className="font-bold">{brands.length}</span> brands categorized
          </div>
          <div className="flex gap-2">
            <span className="px-2 py-1 bg-purple-600 text-white border border-purple-700 rounded-full text-xs font-bold">
              🎨 {indieCount} Indie
            </span>
            <span className="px-2 py-1 bg-pink-600 text-white border border-pink-700 rounded-full text-xs font-bold">
              ✨ {boutiqueCount} Boutique
            </span>
            <span className="px-2 py-1 bg-blue-600 text-white border border-blue-700 rounded-full text-xs font-bold">
              🏪 {mainstreamCount} Mainstream
            </span>
          </div>
          <div className="ml-auto flex gap-3">
            <button
              onClick={handleExport}
              className="text-sm text-glass-secondary hover:text-glass font-medium font-body flex items-center gap-1"
              title="Export your brand categories as JSON"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={handleAutoDetect}
              disabled={saving === 'auto'}
              className="text-sm text-glass-secondary hover:text-glass font-medium font-body"
            >
              {saving === 'auto' ? (
                <span className="flex items-center gap-1">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Detecting...
                </span>
              ) : (
                '✨ Auto-detect brands'
              )}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-600">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-muted z-10" />
            <input
              type="text"
              placeholder="Search brands..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-glass pl-9 py-2 text-sm"
            />
          </div>
        </div>

        {/* Brand list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-glass animate-spin mx-auto mb-2" />
              <p className="text-glass-muted font-body">Loading brands...</p>
            </div>
          ) : sortedBrands.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-glass-muted font-body">
                {searchTerm ? 'No brands match your search' : 'No brands in your collection yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedBrands.map((brand) => {
                const currentCategory = brandCategories[brand];
                const isSaving = saving === brand;

                return (
                  <div
                    key={brand}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-default ${
                      currentCategory
                        ? 'border-slate-600 bg-slate-800/40'
                        : 'border-amber-500/50 bg-amber-500/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-glass font-body">
                        {brand}
                      </span>
                      {!currentCategory && (
                        <span className="text-xs text-amber-400 font-medium">Needs category</span>
                      )}
                    </div>

                    <div className="flex gap-1">
                      {isSaving ? (
                        <Loader2 className="w-5 h-5 text-glass-muted animate-spin" />
                      ) : (
                        <>
                          {BRAND_CATEGORY_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => handleSetCategory(
                                brand,
                                currentCategory === option.value ? null : option.value as BrandCategory
                              )}
                              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                                currentCategory === option.value
                                  ? `${option.color} ring-2 ring-offset-1 ring-current`
                                  : 'bg-slate-700/60 text-glass-muted hover:bg-slate-600/60 hover:text-glass-secondary'
                              }`}
                              title={option.description}
                            >
                              {option.emoji} {option.label}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-600 bg-slate-800/60">
          <button
          onClick={() => {
            onCategoriesUpdated?.();
            onClose();
          }}
          className="w-full py-2 bg-slate-700 text-white rounded-lg font-bold hover:bg-slate-800 transition-default font-body"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandCategoryManager;
